import Fuse, { type IFuseOptions } from "fuse.js";
import type { ProductResponse } from "@/app/api/types/product";

type Product = ProductResponse;

type Partner = {
  name: string;
  usage_name: string | null;
  code: string | null;
  type: number;
};

/**
 * Product search configuration for Fuse.js
 */
const productSearchOptions: IFuseOptions<Product> = {
  keys: [
    { name: "name", weight: 2 },
    { name: "short_name", weight: 1.5 },
    { name: "sku", weight: 1.5 },
    { name: "code", weight: 1 },
  ],
  threshold: 0.4, // Lower = more strict matching
  includeScore: true,
  minMatchCharLength: 2,
};

/**
 * Partner search configuration for Fuse.js
 */
const partnerSearchOptions: IFuseOptions<Partner> = {
  keys: [
    { name: "name", weight: 2 },
    { name: "usage_name", weight: 1.5 },
    { name: "code", weight: 1 },
  ],
  threshold: 0.4,
  includeScore: true,
  minMatchCharLength: 2,
};

/**
 * Search products using exact match prioritization + fuzzy search
 * Priority order:
 * 1. Exact matches on primary fields (name, sku)
 * 2. Exact matches on secondary fields (short_name, code)
 * 3. Fuzzy matches on all fields
 */
export function searchProducts(products: Product[], query: string): Product[] {
  if (!query || query.trim() === "") {
    return products;
  }

  const queryLower = query.toLowerCase().trim();

  // Exact matches on primary fields (name, sku)
  const exactPrimary = products.filter(
    (p) =>
      p.name?.toLowerCase() === queryLower ||
      p.sku?.toLowerCase() === queryLower,
  );

  // Exact matches on secondary fields (short_name, code)
  const exactSecondary = products.filter(
    (p) =>
      !exactPrimary.includes(p) &&
      (p.short_name?.toLowerCase() === queryLower ||
        p.code?.toLowerCase() === queryLower),
  );

  // Fuzzy search on remaining products
  const remaining = products.filter(
    (p) => !exactPrimary.includes(p) && !exactSecondary.includes(p),
  );

  const fuse = new Fuse(remaining, productSearchOptions);
  const fuzzyResults = fuse.search(query).map((r) => r.item);

  // Combine: exact primary, exact secondary, then fuzzy results
  return [...exactPrimary, ...exactSecondary, ...fuzzyResults];
}

/**
 * Search partners using fuzzy search
 */
export function searchPartners<T extends Partner>(
  partners: T[],
  query: string,
): T[] {
  if (!query || query.trim() === "") {
    return partners;
  }

  const fuse = new Fuse(partners, partnerSearchOptions as IFuseOptions<T>);
  const results = fuse.search(query);
  return results.map((result) => result.item);
}

/**
 * Filter products by labels (client-side filtering)
 */
export function filterProductsByLabels(
  products: Product[],
  labels: string[],
): Product[] {
  if (!labels || labels.length === 0) {
    return products;
  }

  return products.filter((product) => {
    if (!product.labels || product.labels.length === 0) {
      return false;
    }
    return labels.every((label) =>
      product.labels?.some((l) => l.name === label),
    );
  });
}

/**
 * Filter partners by type
 */
export function filterPartnersByType<T extends Partner>(
  partners: T[],
  type: number | null,
): T[] {
  if (type === null) {
    return partners;
  }

  return partners.filter((partner) => partner.type === type);
}

/**
 * Combined product filtering and search
 */
export function filterAndSearchProducts(
  products: Product[],
  {
    search,
    labels,
    includeSleeping = false,
    sleepingOnly = false,
    userProductsOnly = false,
  }: {
    search?: string;
    labels?: string[];
    includeSleeping?: boolean;
    sleepingOnly?: boolean;
    userProductsOnly?: boolean;
  },
): Product[] {
  let filtered = products;

  // Filter by user products
  if (userProductsOnly) {
    filtered = filtered.filter((p) => p.is_user_product);
  }

  // Filter by sleeping status
  if (sleepingOnly) {
    filtered = filtered.filter((p) => p.is_sleeping);
  } else if (!includeSleeping) {
    filtered = filtered.filter((p) => !p.is_sleeping);
  }

  // Filter by labels
  if (labels && labels.length > 0) {
    filtered = filterProductsByLabels(filtered, labels);
  }

  // Apply search
  if (search && search.trim() !== "") {
    filtered = searchProducts(filtered, search);
  }

  return filtered;
}

/**
 * Combined partner filtering and search
 */
export function filterAndSearchPartners<T extends Partner>(
  partners: T[],
  {
    search,
    type,
  }: {
    search?: string;
    type?: number | null;
  },
): T[] {
  let filtered = partners;

  // Filter by type
  if (type !== null && type !== undefined) {
    filtered = filterPartnersByType(filtered, type);
  }

  // Apply search
  if (search && search.trim() !== "") {
    filtered = searchPartners(filtered, search);
  }

  return filtered;
}

// Re-export enhanced search functions for new features
export {
  searchProductsEnhanced,
  searchPartnersEnhanced,
  trackProductSelection,
  trackPartnerSelection,
  likelyIsSku as likelyIsSkuEnhanced,
} from "./search/index";
